import React, { useRef, useState, useEffect, useMemo } from 'react'
import Konva from 'konva'
import { v4 as uuidv4 } from 'uuid'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Typography, Button, IconButton, makeStyles,
  Radio, RadioGroup, FormControl, FormControlLabel, FormLabel, Slider, Tooltip
} from '@material-ui/core'

import { Close } from '@material-ui/icons'

import { ConfirmToast } from '../tools'

// ---- CONSTANTS ----- //
const MINIMUM_SHAPE_SIZE = 10
const imageContainerID = 'partial-quarantine-edit-area'
const originalGroupID = 'base-image-group'
const originalImageID = 'original-base-image'
const baseLayerID = 'base-layer'
const boundingGroupID = 'bounding-group'
const idPrefix = {
  SHAPE_GROUP: 'shape-group-',
  IMAGE_NODE: 'image-node-',
  BOUNDING_RECT: 'bounding-rect-',
  TRANSFORMER: 'transformer-node'
}
const konvaNodeTypes = {
  GROUP: 'Group',
  SHAPE: 'Shape',
  IMAGE: 'Image'
}

const filterTypes = {
  PIXELATE: {
    type: 'pixelate',
    label: 'Pixelate',
    konvaFilter: Konva.Filters.Pixelate,
    konvaAttribute: 'pixelSize'
  },
  BLUR: {
    type: 'blur',
    label: 'Blur',
    konvaFilter: Konva.Filters.Blur,
    konvaAttribute: 'blurRadius'
  }
}

// default filter type and size
const filterMin = 15
const defaultFilter = filterTypes.PIXELATE

const SliderTooltip = (props) => {
  const { children, open, value } = props

  return (
    <Tooltip open={open} placement='top' title={value}>
      {children}
    </Tooltip>
  )
}

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    overflowY: 'hidden !important'
  },
  selectionButton: {
    '&:disabled': {
      color: '#84929D !important'
    }
  },
  saveButton: {
    '&:disabled': {
      backgroundColor: '#84929D !important'
    }
  }
}))

const PartialQuarantineModal = (props) => {
  const { partialQuarantineOpen = false, closePartialQuarantineModal = () => {}, baseImageDataStr = '', handlePartialQuarantine = () => {}, capture = {} } = props
  const classes = useStyles()

  const { quarantined = false } = capture

  // local state
  const [imageContainerRef, setImageContainerRef] = useState(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [cursorCrosshair, setCursorCrosshair] = useState(false)
  const [newShapeDimensions, setNewShapeDimensions] = useState(null)
  const [filterSize, setFilterSize] = useState(filterMin)
  const [filterKind, setFilterKind] = useState(defaultFilter)

  // local refs
  const newShapeRef = useRef(null)
  const filterKindRef = useRef(defaultFilter)
  const filterSizeRef = useRef(filterMin)
  const stageLoadedRef = useRef(false)
  const shapesArrRef = useRef([])
  const mouseInAction = useRef(false)
  const baseLayer = useRef(null)
  const boundingGroup = useRef(null)
  const addingTransformerRef = useRef(false)
  const addingShapesRef = useRef(false)
  const boundingTransformer = useRef(null)
  const maxHeightRef = useRef(0)
  // the original dimensions of the image prior to scaling it down for the editor
  const originalImageDimensions = useRef({ height: 0, width: 0, ratio: 0 })
  // the scaled down dimensions of the image
  const scaledImageDimensions = useRef({ height: 0, width: 0, x: 0 })

  const disableActionButtons = Boolean(!baseLayer.current || !baseLayer?.current.find(node => node?.getType() === konvaNodeTypes.GROUP && node?.attrs?.id?.includes(idPrefix.SHAPE_GROUP))?.length)

  // the original image element
  const imageElement = document.createElement('img')

  // if not already loaded, build the konva stage once we have an image container and a containerWidth set
  const stage = useMemo(() => {
    if (imageContainerRef && containerWidth && !stageLoadedRef.current) {
      return new Konva.Stage({
        container: imageContainerID,
        height: maxHeightRef.current,
        width: containerWidth
      })
    } else {
      return null
    }
  }, [imageContainerRef, containerWidth])

  // get the clip area dimensions for the cropped layer group
  const getClip = (shape) => {
    let x = shape.x
    let y = shape.y
    let height = shape.height
    let width = shape.width
    // We potentially get negative values for width and height
    // depending on where the cursor drag starts and ends
    // this lets us correct the positioning of the blurred image
    if (height < 0) {
      height *= -1
      y = y - height
    }
    if (width < 0) {
      width *= -1
      x = x - width
    }

    return { x, y, height, width }
  }

  // Build the dimensions of the stage/canvas
  useEffect(() => {
    if (imageContainerRef && !containerWidth) {
      // set the width to that of the container
      setContainerWidth(imageContainerRef?.clientWidth ?? 0)
      // use the window height, parent container and offset from the top, to calculate the remaining area as the maximum possible height we will allow the container to be
      const parentHeight = imageContainerRef?.offsetParent?.clientHeight
      const parentLastElementHeight = imageContainerRef?.offsetParent?.lastChild?.clientHeight // the height of the dialog actions
      const windowHeight = window?.innerHeight

      // at the time the imageContainerRef loads, its height is 0, therefore we can use the window height, parent height, minus the height of the dialog actions to get the remaining available space we can use for the konva stage
      const maxHeight = windowHeight - parentHeight - parentLastElementHeight

      // set the max height ref to this new calculation
      maxHeightRef.current = maxHeight
    }
  }, [imageContainerRef, containerWidth])

  // Add the image to the stage
  useMemo(() => {
    if (stage && !stageLoadedRef.current) {
      const originalGroup = new Konva.Group({ id: originalGroupID })

      // add the initial layer and group to the stage
      // generate out first stage layer and group
      const layer = new Konva.Layer({ id: baseLayerID })
      baseLayer.current = layer
      stage.add(layer)
      layer.add(originalGroup)

      // init a base for our scaled image height and width
      let scaledImageHeight = 0
      let scaledImageWidth = 0

      // when our image is generated, fetch the original dimensions and scale them according to our container
      imageElement.onload = () => {
        // aspect ratio of the original image
        const imageRatio = imageElement?.height / imageElement?.width
        // determine if the width is longer then the height (usually true)
        const longerWidth = Boolean(imageElement?.width > imageElement?.height)

        // store our original dimensions to use later
        originalImageDimensions.current = { width: imageElement.width, height: imageElement.height, ratio: imageRatio }

        // if the original height is greater then our calculated maximum of the container, then default to our maximum
        scaledImageHeight = imageElement?.height > maxHeightRef.current ? maxHeightRef.current : imageElement.height
        // based on the height result and if the width was originally longer then the height, calculate the scaled width
        scaledImageWidth = longerWidth ? Math.floor(scaledImageHeight / imageRatio) : Math.floor(scaledImageHeight * imageRatio)

        // if after scaling the image down, the width ends up being wider then the container (happens on tall screens), we need to set the width to the maximum allowed width and rescale the height.
        if (scaledImageWidth > containerWidth) {
          scaledImageWidth = containerWidth
          scaledImageHeight = longerWidth ? Math.floor(scaledImageWidth * imageRatio) : Math.floor(scaledImageWidth / imageRatio)
        }

        // using the newly calculated width, find the x offset to ensure the image is as closely centered as possible
        const xOffset = Math.floor((containerWidth - scaledImageWidth) / 2)

        scaledImageDimensions.current = { height: scaledImageHeight, width: scaledImageWidth, x: xOffset }

        // using the calculations generate a konva image
        const originalImage = new Konva.Image({
          id: originalImageID,
          image: imageElement,
          height: scaledImageHeight,
          width: scaledImageWidth,
          x: xOffset,
          y: 0
        })
        // add the generated image instance into the group
        originalGroup.add(originalImage)
      }

      // assign the source of the image as the base capture violation image
      imageElement.src = baseImageDataStr // change this to base64 once implemented

      // signal we have completed loading our stage (prevents re-renders)
      stageLoadedRef.current = true
    }
  }, [stage, imageElement, baseImageDataStr, containerWidth])

  // Generate shapes on the canvas
  useMemo(() => {
    if (newShapeDimensions && Object.keys(newShapeDimensions).length) {
      // signal we are modifying the shapes
      addingShapesRef.current = true
      // find all group currently on the layer
      const allGroups = stage?.find(node => node?.getType() === konvaNodeTypes.GROUP)
      // check to see if we already have a bounding box group
      const existingBoundingGroup = allGroups?.find(groupClass => groupClass?.attrs?.id === boundingGroupID)
      // if not, create one and set it to the ref
      if (!existingBoundingGroup) { boundingGroup.current = new Konva.Group({ id: boundingGroupID }) }
      const shape = { ...newShapeDimensions }
      // if the newly drawn shape has not already rendered, generate a clipped group of the image edited with the desired filter, as well as a bounding box
      const shapeID = shape.id
      // individual groupID for holding the group holding the blurred image
      const groupID = `${idPrefix.SHAPE_GROUP}${shapeID}`
      const groupExists = Boolean(allGroups?.length && allGroups?.find(groupClass => groupClass?.attrs?.id === groupID)) // checks for the individual clipped image group
      const validSize = Boolean(Math.abs(shape.height) >= MINIMUM_SHAPE_SIZE && Math.abs(shape.width) >= MINIMUM_SHAPE_SIZE) // checks the shape is a valid size

      // if the shape has not yet been rendered, and is a valid size, generate the images and shapes
      if (!groupExists && stage && validSize) {
        // generate a new clipped group for the blurred image
        const shapeGroup = new Konva.Group({ id: groupID }) // create a new group for the image
        const groupClip = getClip(shape)

        // Attach the original image to the group
        const groupImage = new Konva.Image({
          id: `${idPrefix.IMAGE_NODE}${shape.id}`,
          image: imageElement,
          ...scaledImageDimensions.current
        })
        imageElement.src = baseImageDataStr

        // cache and filter the image by the desired censor type (blurred or pixelated)
        groupImage.cache()
        const filter = filterKindRef?.current?.konvaFilter || defaultFilter.konvaFilter
        const filterAttribute = filterKindRef.current.konvaAttribute || defaultFilter.konvaAttribute
        groupImage.filters([filter])
        groupImage[filterAttribute](filterSizeRef.current || filterMin)
        shapeGroup.add(groupImage)

        // Create the Bounding Rectangle (this will allow the transformer box to have something to more easily transform the section of the blurred image being shown)
        const boundingRect = new Konva.Rect({
          id: `${idPrefix.BOUNDING_RECT}${shape.id}`,
          ...groupClip,
          fill: 'transparent',
          draggable: true,
          cornerRadius: 3
        })

        // add the bounding rectangle handlers
        boundingRect.on('click', () => { if (boundingTransformer.current) { boundingTransformer.current.nodes([boundingRect]) } })
        boundingRect.on('dragstart', () => { if (boundingTransformer.current) { boundingTransformer.current.nodes([boundingRect]) } })
        // on drag do not allow the shape to be moved outside of the bounds of the base image
        boundingRect.on('dragmove', () => {
          let x = boundingRect.x()
          let y = boundingRect.y()
          const trueHeight = boundingRect.height() * boundingRect.scaleY()
          const trueWidth = boundingRect.width() * boundingRect.scaleX()
          const imageWidthFromX0 = scaledImageDimensions?.current.width + scaledImageDimensions?.current.x

          if (x <= scaledImageDimensions?.current.x) { x = scaledImageDimensions?.current.x }
          if (x + trueWidth >= imageWidthFromX0) { x = imageWidthFromX0 - trueWidth }
          if (y <= 0) { y = 0 }
          if (y + trueHeight >= scaledImageDimensions?.current.height) { y = scaledImageDimensions?.current.height - trueHeight }
          boundingRect.y(y)
          boundingRect.x(x)
          shapeGroup.clipX(x)
          shapeGroup.clipY(y)
        })

        // add the new rectangle to the bounding boxes group
        boundingGroup.current.add(boundingRect)
        // change the blurred images clipped section based on the bounding rectangle shape current dimensions
        shapeGroup.clipFunc((ctx) => {
          ctx.save()
          ctx.translate(boundingRect.x(), boundingRect.y())
          ctx.rect(0, 0, boundingRect.width() * boundingRect.scaleX(), boundingRect.height() * boundingRect.scaleY())
          ctx.restore()
        })
        if (baseLayer.current) {
          // add the new blurred image group to the layer
          baseLayer.current.add(shapeGroup)
          // if we have not yet added the bounding group add it to the layer
          if (!existingBoundingGroup) { baseLayer.current.add(boundingGroup.current) }
          boundingGroup.current.moveToTop()
          // if we have generated a transformer, attach it to the new rect shape
          if (boundingTransformer.current) { boundingTransformer.current.nodes([boundingRect]) }
          // redraw the image
          baseLayer.current.batchDraw()
        }
      }

      setNewShapeDimensions(null)

      // signal we are done modifying shapes
      addingShapesRef.current = false
      return true
    }
  }, [stage, baseImageDataStr, imageElement, newShapeDimensions])

  // Add a transformer to the selected shapes bounding rect.
  useMemo(() => {
    const existingTransformer = Boolean(boundingTransformer.current)
    if (stage && !existingTransformer) {
      // generate the new transformer
      const newTransformerNode = new Konva.Transformer({
        id: `${idPrefix.TRANSFORMER}`,
        rotateEnabled: false,
        boundBoxFunc: (oldBox, newBox) => {
          // do not allow the box to be transformed smaller than the min
          if (Math.abs(newBox.width) <= MINIMUM_SHAPE_SIZE || Math.abs(newBox.height) <= MINIMUM_SHAPE_SIZE) { return oldBox }
          return newBox
        }
      })

      // NOTE: the transformer is changing scale of the node
      // and NOT its width or height

      // transformer function to now allow the box to be made greater then the dimensions of the base image
      newTransformerNode.on('transform', (e) => {
        // the current node the transformer is active on
        const selectedBoundingRect = e.target

        // width and height relative to the scale
        const baseWidth = selectedBoundingRect.width()
        const trueWidth = baseWidth * selectedBoundingRect.scaleX()

        const baseHeight = selectedBoundingRect.height()
        const trueHeight = baseHeight * selectedBoundingRect.scaleY()

        // check if we have transformed the shape into smaller than the allowed limit
        const tooShort = Boolean(trueHeight < MINIMUM_SHAPE_SIZE)
        const tooThin = Boolean(trueWidth < MINIMUM_SHAPE_SIZE)
        if (tooShort || tooThin) {
          newTransformerNode.stopTransform()
          return
        }

        // check if the box is being dragged to far to the right or left
        const relativeWidthOffset = scaledImageDimensions?.current.width + scaledImageDimensions?.current.x
        const rectTooLongOnRight = Boolean((trueWidth + selectedBoundingRect.x()) > relativeWidthOffset)
        const rectTooLongOnLeft = Boolean(selectedBoundingRect.x() <= scaledImageDimensions?.current.x)
        const widerThanImage = Boolean(rectTooLongOnRight || rectTooLongOnLeft)

        // check of the box is being extended above or below the image
        const rectLongerTop = Boolean(selectedBoundingRect.y() <= 0)
        const rectLongerBottom = Boolean((trueHeight + selectedBoundingRect.y()) >= scaledImageDimensions?.current.height)
        const tallerThanImage = Boolean(rectLongerTop || rectLongerBottom)

        // if the image has gone beyond it boundaries, stop the transformation and adjust it back slightly
        if (widerThanImage || tallerThanImage) {
          // stop transformation
          newTransformerNode.stopTransform()

          // these adjustments prevent the transformer from being just out of reach without significantly altering the user's selection
          if (widerThanImage) {
            if (rectTooLongOnLeft) { selectedBoundingRect.x(scaledImageDimensions?.current.x + 1); selectedBoundingRect.width(trueWidth - 1) }
            if (rectTooLongOnRight) { selectedBoundingRect.x(selectedBoundingRect.x() - 1) }
            // determine the new x scale
            const diffOffsetX = selectedBoundingRect.x() - scaledImageDimensions?.current.x
            const maxWidthRect = rectTooLongOnRight ? scaledImageDimensions?.current.width - diffOffsetX : baseWidth
            const newScaleX = Math.floor(maxWidthRect) / baseWidth
            selectedBoundingRect.scaleX(newScaleX)
          }
          if (tallerThanImage) {
            if (rectLongerTop) { selectedBoundingRect.y(1) }
            if (rectLongerBottom) { selectedBoundingRect.y(selectedBoundingRect.y() - 1) }
          }
        }
      })

      // it is possible to move the transformer fast enough that it causes an axis cross over before the transformer can be stopped
      // so on transform end, check to be sure it was not accidentally inverted
      newTransformerNode.on('transformend', (e) => {
        // find the current target and scale
        const boundingRect = e.target
        let scaleX = boundingRect.scaleX()
        let scaleY = boundingRect.scaleY()

        // if the inversion occurred, it causes the shape to have a rotation as well and we need to reset it back to -0
        if (!Object.is(boundingRect.rotation(), -0)) {
          boundingRect.rotation(-0)
        }
        // inversion will also cause the scale to become a negative. Reset the scales to their position prior to inversion
        if (scaleX < 0) { scaleX *= -1 }
        if (scaleY < 0) { scaleY *= -1 }
        boundingRect.scaleX(scaleX)
        boundingRect.scaleY(scaleY)
      })

      // once we have a transformer built, add it to the base layer, connect it to the selected shape and redraw the canvas
      if (baseLayer.current) {
        boundingTransformer.current = newTransformerNode
        baseLayer.current.add(newTransformerNode)
        baseLayer.current.batchDraw()
      }
    }
  }, [stage])

  // add all needed click handlers for the stage once it has loaded
  useMemo(() => {
    if (stage) {
      const handleAddMask = () => {
        const pointerPosition = stage.getPointerPosition()
        if (!pointerPosition) {
          return null
        }

        const id = uuidv4()
        newShapeRef.current = {
          id,
          x: pointerPosition.x,
          y: pointerPosition.y,
          width: 0,
          height: 0
        }
        return id
      }

      const handleDragStart = (e) => {
        if (!mouseInAction.current) {
          mouseInAction.current = true
          if (boundingTransformer.current) {
            boundingTransformer.current.nodes([])
          }
          const clickedOnEmpty = e.target.index === 0
          if (!clickedOnEmpty) {
            return
          }
          handleAddMask()
        }
      }

      const handleDragMove = () => {
        if (mouseInAction.current) {
          const pointerPosition = stage.getPointerPosition()
          if (!pointerPosition) {
            return
          }

          if (newShapeRef.current) {
            newShapeRef.current.width = pointerPosition.x - newShapeRef.current.x
            newShapeRef.current.height = pointerPosition.y - newShapeRef.current.y
          }
        }
      }

      const handleDragEnd = (e) => {
        if (mouseInAction.current) {
          // Ignore if mask is too tiny
          const validSizeShape = Boolean(Math.abs(newShapeRef.current.width) > MINIMUM_SHAPE_SIZE && Math.abs(newShapeRef.current.height) > MINIMUM_SHAPE_SIZE)
          if (validSizeShape) {
            setNewShapeDimensions(newShapeRef.current)
          }
          mouseInAction.current = false
        }
      }

      if (stage.eventListeners) {
        const listeners = stage.eventListeners
        // cursor change on hover
        if (!listeners.mouseover) { stage.on('mouseover', konvaNodeTypes.IMAGE, (e) => { if (!cursorCrosshair) { setCursorCrosshair(true) } }) }
        if (!listeners.mouseout) { stage.on('mouseout', konvaNodeTypes.IMAGE, (e) => { setCursorCrosshair(false) }) }

        // dragging functionality
        if (!listeners.mousedown) { stage.on('mousedown', konvaNodeTypes.IMAGE, (e) => handleDragStart(e)) }
        if (!listeners.mousemove) { stage.on('mousemove', (e) => handleDragMove(e)) }
        if (!listeners.mouseup) { stage.on('mouseup', (e) => handleDragEnd(e)) }
      }
    }
  }, [stage, cursorCrosshair])

  // clear all current shapes and nodes
  const handleClearSelections = () => {
    if (baseLayer.current) {
      const shapeGroups = baseLayer.current.find(node => node.getType() === konvaNodeTypes.GROUP && node.attrs.id.includes(idPrefix.SHAPE_GROUP))
      if (boundingGroup.current) { boundingGroup.current.destroy() }
      if (boundingTransformer.current) { boundingTransformer.current.nodes([]) }
      if (shapeGroups.length) { shapeGroups.forEach(node => node.destroy()) }
      baseLayer.current.batchDraw()
    }
  }

  // reset all/close modal
  const handleReset = () => {
    closePartialQuarantineModal()
    // reset all refs
    stageLoadedRef.current = false
    filterKindRef.current = defaultFilter
    filterSizeRef.current = filterMin
    shapesArrRef.current = []
    mouseInAction.current = false
    newShapeRef.current = null
    baseLayer.current = null
    maxHeightRef.current = 0
    boundingGroup.current = null
    addingTransformerRef.current = false
    addingShapesRef.current = false
    boundingTransformer.current = null
    originalImageDimensions.current = { height: 0, width: 0, ratio: 0 }
    scaledImageDimensions.current = { height: 0, width: 0, x: 0 }

    // reset state
    setImageContainerRef(null)
    setContainerWidth(0)
    setCursorCrosshair(false)
    setNewShapeDimensions(null)
    setFilterKind(defaultFilter)
    setFilterSize(filterMin)

    // destroy the canvas
    if (stage) {
      stage.destroy()
    }
  }

  const handleSave = async () => {
    // hide the transformer if present
    if (boundingTransformer.current) {
      boundingTransformer.current.destroy()
      boundingTransformer.current = null
    }

    let confirmedQuarantine = true

    // if not already quarantined, have the supervisor confirm their decision before proceeding
    if (!quarantined) {
      confirmedQuarantine = await ConfirmToast({ msg: 'Warning: This action blocks the image from being viewed by anyone except for supervisors with the proper elevated permissions.', txt: 'Are you sure you want to quarantine this violation?' })
    }

    if (confirmedQuarantine) {
      // grab the original and scaled dimension of the violation image
      const { x: scaledX, height: scaledHeight, width: scaledWidth } = scaledImageDimensions.current
      const { height: originalHeight, width: originalWidth } = originalImageDimensions.current

      if (baseLayer.current) {
        // find the image node
        const originalImage = stage.findOne(node => node.getType() === konvaNodeTypes.SHAPE && node.attrs.id === originalImageID)
        // find the group holding the bounding boxes
        const boundingRects = boundingGroup.current.getChildren(node => node.getType() === konvaNodeTypes.SHAPE)
        // find the blurred images behind the bounding boxes
        const shapeRects = stage.find(node => node.getType() === konvaNodeTypes.SHAPE && node.attrs.id.includes(idPrefix.IMAGE_NODE))

        // set the image back to 0 x position and the bounding boxes and their blurred counterparts back by the relative difference
        // this helps to accurately grab the canvas data
        if (originalImage && boundingRects?.length && shapeRects?.length) {
          originalImage.x(0)
          boundingRects.forEach(node => {
            const currentX = node.x()
            node.x(currentX - scaledX)
          })

          shapeRects.forEach(node => {
            const currentX = node.x()
            node.x(currentX - scaledX)
          })
        }

        // find the new scale, and up the scale of the entire stage
        const newScaleX = originalWidth / scaledWidth
        const newScaleY = originalHeight / scaledHeight
        stage.scale({ x: newScaleX, y: newScaleY })

        // get the true height and width of the stage (not just the viewport)
        const stageRect = stage.getClientRect()
        // set the config of the data url to get the full stage height and width and not just the viewport
        const config = {
          width: stageRect.width,
          height: stageRect.height
        }

        // fetch the base64
        const base64 = stage.toDataURL(config)
        handlePartialQuarantine(base64)
        handleReset()
      }
    }
  }

  // change the filter type and size
  const handleFilterChange = (filter = null, size = null) => {
    // set the correct local values based on if we are changing the filter type or the size
    const filterStateFunc = filter ? setFilterKind : setFilterSize
    const filterRef = filter ? filterKindRef : filterSizeRef

    // determine the correct values to use based on if we are setting the filter type or size
    const newFilter = filter ? Object.values(filterTypes).find((obj) => obj.type === filter) : filterKind
    const newSize = size || filterSize
    const setValue = filter ? newFilter : size

    // set the state and ref
    filterStateFunc(setValue)
    filterRef.current = setValue

    // find any image nodes (other then the base) on the stage and modify each of their filters to the desired change
    if (stage) {
      const imageGroups = stage.find(node => node.getType() === konvaNodeTypes.SHAPE && node.attrs?.id?.includes(idPrefix.IMAGE_NODE))
      imageGroups.forEach(node => {
        if (filter) {
          node.filters([newFilter.konvaFilter])
        }
        node[newFilter.konvaAttribute](newSize)
      })
    }
  }

  return (
    <Dialog open={partialQuarantineOpen} onClose={() => handleReset()} fullWidth maxWidth='lg'>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Grid container item direction='row'>
          <Grid item container xs={8}>
            <Typography
              variant='h5'
              color='primary'
              style={{ fontWeight: 600, color: '#3984f7' }}
            >
              Partial Quarantine
            </Typography>
          </Grid>
          <Grid item container xs={4} style={{ justifyContent: 'flex-end' }}>
            <IconButton
              style={{ padding: 0 }}
              onClick={() => handleReset()}
            >
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <Grid item container direction='column'>
          <Grid item container direction='row'>
            <Grid item container xs={8}>
              <Typography variant='body1'>Use your mouse to select the area of the capture violation below that you would like to obscure. Once you have completed your selections you can save the result using the buttons below.</Typography>
            </Grid>
            <Grid item container xs={4} style={{ justifyContent: 'flex-end', alignItems: 'flex-end' }}>
              <Button
                variant='text'
                style={{ fontWeight: 600, color: '#3984f7' }}
                classes={{ root: classes.selectionButton }}
                disabled={disableActionButtons}
                onClick={handleClearSelections}
              >
                Clear Selections
              </Button>
            </Grid>
          </Grid>
          <Grid item container direction='column' style={{ marginTop: '1em' }}>
            <Grid item container>
              <Typography variant='subtitle2'>You can change the filter type and level of obscurity using the options below.</Typography>
            </Grid>
            <Grid item container direction='row' alignItems='center' justify='flex-start' style={{ marginTop: '.5em' }}>
              <Grid item container xs={2}>
                <FormControl>
                  <FormLabel style={{ marginBottom: 0 }}>Filter Type</FormLabel>
                  <RadioGroup row value={filterKind.type} onChange={(e) => handleFilterChange(e.target.value)}>
                    {Object.values(filterTypes).map((value) => {
                      return (
                        <FormControlLabel key={`filter-type-${value.type}`} value={value.type} label={value.label} control={<Radio size='small' checked={filterKind === value} />} style={{ marginBottom: 0 }} />
                      )
                    })}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item container xs={10}>
                <FormControl>
                  <FormLabel>Obscurity Level</FormLabel>
                  <Slider
                    value={filterSize}
                    min={filterMin}
                    max={100}
                    onChange={(e, newValue) => handleFilterChange(null, newValue)}
                    ValueLabelComponent={SliderTooltip}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          <Grid item container style={{ marginTop: '1em', backgroundColor: '#e0e2e3', padding: '1em', borderRadius: '5px' }}>
            <Grid ref={setImageContainerRef} id={imageContainerID} item style={{ height: '100%', width: '100%', maxHeight: '100%', cursor: cursorCrosshair ? 'crosshair' : 'auto' }} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: '10px 24px' }}>
        <Button
          variant='text'
          style={{ fontWeight: 600, textTransform: 'none' }}
          onClick={() => handleReset()}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          style={{ backgroundColor: '#3984f7', color: 'white', fontWeight: 600, textTransform: 'none' }}
          onClick={handleSave}
          disabled={disableActionButtons}
          classes={{ root: classes.saveButton }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PartialQuarantineModal
